/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { createSelector, createSlice } from '@reduxjs/toolkit';
import { ThunkStatus } from '../../../enums/ThunkStatus';
import { deleteRemoveUserAccountThunk, deleteValidateRemoveUserThunk } from './user.remove.thunk';

/* Initial State */
const initialState = {
  status: ThunkStatus.IDLE,
  userId: undefined,
  confirmDialog: { open: false },
  verifyDialog: { open: false },
  successDialog: { open: false },
  errorDialog: { open: false },
  error: undefined,
};

/* Slice */
export const removeUserSlice = createSlice({
  name: 'removeUser',
  initialState,
  reducers: {
    toggleRemoveUserConfirmDialog(state, action) {
      state.errorDialog.open = false;
      state.confirmDialog.open = action.payload;
    },
    toggleVerifyDialog(state, action) {
      state.verifyDialog.open = action.payload;
    },
    toggleSuccessDialog(state, action) {
      state.successDialog.open = action.payload;
    },
    toggleErrorDialog(state, action) {
      state.errorDialog.open = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(deleteValidateRemoveUserThunk.pending, (state) => {
        state.status = ThunkStatus.LOADING;
      })
      .addCase(deleteValidateRemoveUserThunk.fulfilled, (state) => {
        state.status = ThunkStatus.SUCCEEDED;
        state.confirmDialog.open = true;
        state.error = undefined;
      })
      .addCase(deleteValidateRemoveUserThunk.rejected, (state, action) => {
        state.status = ThunkStatus.FAILED;
        state.errorDialog.open = true;
        state.error = action?.error?.message;
      })
      .addCase(deleteRemoveUserAccountThunk.pending, (state) => {
        state.status = ThunkStatus.LOADING;
      })
      .addCase(deleteRemoveUserAccountThunk.fulfilled, (state) => {
        state.status = ThunkStatus.SUCCEEDED;
        state.verifyDialog.open = false;
        state.successDialog.open = true;
        state.error = undefined;
      })
      .addCase(deleteRemoveUserAccountThunk.rejected, (state, action) => {
        state.status = ThunkStatus.FAILED;
        state.verifyDialog.open = false;
        state.successDialog.open = true;
        state.error = action?.error?.message;
      });
  },
});

/* Selectors */
const selectRemoveUserSlice = state => state[removeUserSlice.name];

export const selectConfirmDialog = createSelector(selectRemoveUserSlice, s => s.confirmDialog);
export const selectStatus = createSelector(selectRemoveUserSlice, s => s.status);
export const selectUserId = createSelector(selectRemoveUserSlice, s => s.userId);
export const selectError = createSelector(selectRemoveUserSlice, s => s.error);
export const selectVerifyDialog = createSelector(selectRemoveUserSlice, s => s.verifyDialog);
export const selectErrorDialog = createSelector(selectRemoveUserSlice, s => s.errorDialog);
export const selectSuccessDialog = createSelector(selectRemoveUserSlice, s => s.successDialog);

/* Actions */
export const {
  toggleRemoveUserConfirmDialog,
  updateUserId,
  toggleErrorDialog,
  toggleVerifyDialog,
  toggleSuccessDialog,
} = removeUserSlice.actions;
